/**
 * StreamingFlyoutContext -- Shared flyout state for all streaming subpages.
 *
 * Allows Analytics, Logs, and Settings flyout panels to open from any page
 * under /streaming/dashboard/* without navigating away from the current page.
 *
 * The actual SideFlyout rendering lives in StreamingLayout.tsx (the layout route).
 */
import { createContext, useContext, useCallback, useState, type ReactNode } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type StreamingFlyoutMode = "analytics" | "settings" | "logs" | null;

interface StreamingFlyoutContextValue {
  flyoutMode: StreamingFlyoutMode;
  openFlyout: (mode: Exclude<StreamingFlyoutMode, null>) => void;
  closeFlyout: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const StreamingFlyoutCtx = createContext<StreamingFlyoutContextValue | null>(null);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useStreamingFlyout = (): StreamingFlyoutContextValue => {
  const ctx = useContext(StreamingFlyoutCtx);
  if (!ctx) {
    throw new Error("useStreamingFlyout must be used within StreamingFlyoutProvider");
  }
  return ctx;
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const StreamingFlyoutProvider = ({ children }: { children: ReactNode }) => {
  const [flyoutMode, setFlyoutMode] = useState<StreamingFlyoutMode>(null);

  const openFlyout = useCallback((mode: Exclude<StreamingFlyoutMode, null>) => {
    setFlyoutMode(mode);
  }, []);

  const closeFlyout = useCallback(() => {
    setFlyoutMode(null);
  }, []);

  return (
    <StreamingFlyoutCtx.Provider value={{ flyoutMode, openFlyout, closeFlyout }}>
      {children}
    </StreamingFlyoutCtx.Provider>
  );
};
